import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

/*
|--------------------------------------------------------------------------
| GET DETAIL TENDER
|--------------------------------------------------------------------------
| GET /internal/tenders/{id}
*/

export const fetchTenderDetail = createAsyncThunk(
    "internalTender/fetchTenderDetail",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${id}`
            );

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to load tender detail."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| UPDATE TANGGAL AANWIJZING
|--------------------------------------------------------------------------
| PUT /internal/tenders/{id}/aanwijzing
*/

export const updateTenderAanwijzing = createAsyncThunk(
    "internalTender/updateTenderAanwijzing",
    async ({ tenderId, aanwijzing_start_at, aanwijzing_end_at }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${tenderId}/aanwijzing`,
                { aanwijzing_start_at, aanwijzing_end_at }
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Gagal menyimpan tanggal Aanwijzing."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| UPDATE PROGRESS TENDER
|--------------------------------------------------------------------------
| POST /internal/tenders/{id}/progress
*/

export const updateTenderProgress = createAsyncThunk(
    "internalTender/updateTenderProgress",
    async (
        {
            tenderId,
            stage_step,
            notes,
            file,
            aanwijzing_start_at,
            aanwijzing_end_at,
            batas_penawaran_date,
        },
        { rejectWithValue }
    ) => {
        try {
            const formData = new FormData();

            formData.append("stage_step", stage_step);
            formData.append("notes", notes ?? "");

            if (file) {
                formData.append("file", file);
            }

            if (aanwijzing_start_at) {
                formData.append("aanwijzing_start_at", aanwijzing_start_at);
            }
            if (aanwijzing_end_at) {
                formData.append("aanwijzing_end_at", aanwijzing_end_at);
            }
            if (batas_penawaran_date) {
                formData.append("batas_penawaran_date", batas_penawaran_date);
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${tenderId}/progress`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to update progress."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| GET TIMELINE
|--------------------------------------------------------------------------
*/

export const fetchTenderTimeline = createAsyncThunk(
    "internalTender/fetchTimeline",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${id}/timeline`
            );

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to load timeline."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| GET REGISTERED VENDORS
|--------------------------------------------------------------------------
*/

export const fetchTenderVendors = createAsyncThunk(
    "internalTender/fetchVendors",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${id}/vendors`
            );

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to load vendors."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| GET SLA HISTORY
|--------------------------------------------------------------------------
*/

export const fetchTenderSLAHistory = createAsyncThunk(
    "internalTender/fetchSLAHistory",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${id}/sla-history`
            );

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to load SLA history."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| DOWNLOAD DOCUMENT
|--------------------------------------------------------------------------
*/

export const downloadTenderDocument = createAsyncThunk(
    "internalTender/downloadDocument",
    async (
        {
            tenderId,
            documentId,
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/tenders/${tenderId}/documents/${documentId}`,
                {
                    responseType: "blob",
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to download document."
            );
        }
    }
);

export const fetchTenderCategories = createAsyncThunk(
    "internalTender/fetchTenderCategories",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/internal-tender/categories`);

            return res.data.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to load categories"
            );
        }
    }
);

export const fetchTenderType = createAsyncThunk(
    "internalTender/fetchTenderType",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/internal-tender/tender-types`);

            return res.data.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to load tender types"
            );
        }
    }
);

export const fetchVendorOptions = createAsyncThunk(
    "internalTender/fetchVendorOptions",
    async ({ search = "", page = 1 }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal-tender/daftar-vendors`,
                {
                    params: {
                        search,
                        page,
                    },
                }
            );

            return {
                search,
                page,
                vendors: response.data.data.data,
                currentPage: response.data.data.current_page,
                lastPage: response.data.data.last_page,
            };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to load vendors."
            );
        }
    }
);

export const fetchDashboard = createAsyncThunk(
    "internalTender/fetchDashboard",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/internal/dashboard`
            );

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to load dashboard."
            );
        }
    }
);

export const submitTender = createAsyncThunk(
    "internalTender/submitTender",
    async ({ tenderId }, { rejectWithValue }) => {
        try {

            const response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/internal-tenders/${tenderId}/submit`
            );

            return response.data.data;

        } catch (error) {

            return rejectWithValue(
                error.response?.data?.message || "Failed to submit tender."
            );

        }
    }
);

export const updateTenderDraft = createAsyncThunk(
    "internalTender/updateTenderDraft",
    async (
        {
            tenderId,
            data,
        },
        { rejectWithValue }
    ) => {
        try {

            const formData = new FormData();

            formData.append("title", data.title);
            formData.append("description", data.description ?? "");
            formData.append("category_id", data.categoryId);
            formData.append("jenis_id", data.jenisId);
            formData.append("budget", data.budget);
            formData.append("notes", data.notes ?? "");
            formData.append("deadline", data.deadline);

            if (data.ursFile) {

                formData.append(
                    "urs_file",
                    data.ursFile
                );

            }

            if (data.vendors) {

                data.vendors.forEach((vendor) => {

                    formData.append(
                        "vendors[]",
                        vendor.id ?? vendor
                    );

                });

            }

            formData.append("_method", "PUT");

            const response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/internal-tenders/${tenderId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            return response.data.data;

        } catch (error) {

            return rejectWithValue(
                error.response?.data?.message ||
                "Failed to update draft."
            );

        }
    }
);

export const addTenderCategory = createAsyncThunk(
    "internalTender/addTenderCategory",
    async ({ name, createdBy = null }, { rejectWithValue }) => {
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/internal-tender/categories`,
                {
                    name,
                    created_by: createdBy,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            return res.data.data;
        } catch (err) {
            // Ambil pesan validasi pertama dari Laravel (422)
            const firstError =
                err.response?.data?.errors &&
                Object.values(err.response.data.errors)[0];

            const message =
                (Array.isArray(firstError) ? firstError[0] : firstError) ||
                err.response?.data?.message ||
                "Gagal menyimpan kategori.";

            return rejectWithValue(message);
        }
    }
);